import React, { useEffect, useState } from 'react';
import {
  Box, IconButton, Tooltip, Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { matchPlayerApi, playerApi } from '../../services/api';
import type { MatchPlayer, Player } from '../../models';
import MatchPlayerDialog, { type MatchPlayerFormValues } from '../../components/Cricket/MatchPlayerDialog';

interface MatchPlayersTableProps {
  matchId: number;
}

const roleOptions = [
  { value: 'BAT', label: 'BAT' },
  { value: 'BOWL', label: 'BOWL' },
  { value: 'ALL', label: 'ALL' },
  { value: 'WK', label: 'WK' },
];

const lineupStatusOptions = [
  { value: 'PLAYING', label: 'Playing' },
  { value: 'NOT_PLAYING', label: 'Not Playing' },
  { value: 'LAST_PLAYED', label: 'Last Played' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
  { value: 'X_FACTOR_SUBSTITUTE', label: 'X-Factor Substitute' },
  { value: 'USED_X_FACTOR_SUBSTITUTE', label: 'Used X-Factor Substitute' },
];


const MatchPlayersTable: React.FC<MatchPlayersTableProps> = ({ matchId }) => {
  const [matchPlayers, setMatchPlayers] = useState<MatchPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatchPlayer, setSelectedMatchPlayer] = useState<MatchPlayer | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitialValues, setDialogInitialValues] = useState<MatchPlayerFormValues | null>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await playerApi.getAll();
        if (response.data && response.data.results) {
          setAvailablePlayers(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };
    fetchPlayers();
  }, []);

  const columns: GridColDef[] = [
    { field: 'prev_bat_pos', headerName: 'BP', width: 50, sortable: true, filterable: false, type: 'number' },
    { field: 'player', headerName: 'Player', width: 200, sortable: true, valueGetter: (params: any) => params?.name || '' },
    { field: 'd_id', headerName: 'D ID', width: 80 },
    { field: 'mt_id', headerName: 'MT ID', width: 80 },
    { field: 'c_id', headerName: 'C ID', width: 80 },
    { field: 'team_name', headerName: 'Team', width: 100, sortable: true },
    { field: 'role', headerName: 'Role', width: 100, sortable: true, filterable: true, type: 'singleSelect', valueOptions: roleOptions.map(opt => opt.value), renderCell: (params: any) => { const role = roleOptions.find(r => r.value === params.value); return role ? role.label : params.value; } },
    { field: 'lineup_status', headerName: 'Status', width: 100, sortable: true, filterable: true, type: 'singleSelect', valueOptions: lineupStatusOptions.map(opt => opt.value), renderCell: (params) => { const status = lineupStatusOptions.find(s => s.value === params.value); const statusLabel = status ? status.label : params.value; return (<Box sx={{ backgroundColor: params.value === 'PLAYING' ? '#4caf50' : params.value === 'NOT_PLAYING' ? '#f44336' : params.value === 'UNAVAILABLE' ? '#9e9e9e' : params.value === 'LAST_PLAYED' ? '#ff9800' : '#2196f3', color: 'white', padding: '0px 6px', fontSize: '0.75rem', borderRadius: '10px', textTransform: 'capitalize', display: 'inline-block', minWidth: '60px', textAlign: 'center', }}>{statusLabel}</Box>); }, },
    { field: 'credits', headerName: 'C', width: 50, sortable: true, type: 'number' },
    { field: 'selected_perc', headerName: 'S%', width: 70, sortable: true, type: 'number' },
    { field: 'captain_perc', headerName: 'C%', width: 70, sortable: true, type: 'number' },
    { field: 'vice_captain_perc', headerName: 'VC%', width: 70, sortable: true, type: 'number' },
    { field: 'series_points', headerName: 'Pts', width: 70, sortable: true, type: 'number' },
    { field: 'series_avg_points', headerName: 'A Pt', width: 70, sortable: true, type: 'number' },
    { field: 'in_dream_team', headerName: 'DT', width: 70, sortable: true, type: 'boolean', renderCell: (params) => params.value ? 'Yes' : 'No' },
    { field: 'actions', headerName: 'Actions', width: 150, sortable: false, filterable: false, renderCell: (params: any) => (<Box><Tooltip title="Edit"><IconButton color="primary" size="small" onClick={() => handleEdit(params.row)}><EditIcon /></IconButton></Tooltip><Tooltip title="Delete"><IconButton color="error" size="small" onClick={() => handleDelete(params.row.id)}><DeleteIcon /></IconButton></Tooltip><Tooltip title="Refresh Player Stats"><IconButton color="info" size="small" onClick={() => handleRefreshPlayerStats(params.row.player.id)}><RefreshIcon /></IconButton></Tooltip></Box>), },
  ];

  const [page, _setPage] = useState(1);
  const pageSize = 50;

  const fetchMatchPlayers = React.useCallback(async (pageNumber: number = 1, size: number = pageSize) => {
    try {
      setLoading(true);
      const params: any = {
        page: pageNumber,
        page_size: size,
        match: matchId,
      };
      const response = await matchPlayerApi.getAll(params);
      setMatchPlayers(response.data.results);
    } catch (error) {
      console.error('Error fetching match players:', error);
    } finally {
      setLoading(false);
    }
  }, [matchId, pageSize]);

  useEffect(() => {
    fetchMatchPlayers(page, pageSize);
  }, [page, pageSize, fetchMatchPlayers]);

  const handleEdit = (matchPlayer: MatchPlayer) => {
    setSelectedMatchPlayer(matchPlayer);
    setDialogInitialValues({
      id: matchPlayer.id,
      player: matchPlayer.player,
      match: matchPlayer.match || null,
      team: matchPlayer.team ? { id: matchPlayer.team } : null,
      role: matchPlayer.role || 'BAT',
      lineup_status: matchPlayer.lineup_status || 'NOT_PLAYING',
      credits: matchPlayer.credits || 0,
      selected_perc: matchPlayer.selected_perc || 0,
      captain_perc: matchPlayer.captain_perc || 0,
      vice_captain_perc: matchPlayer.vice_captain_perc || 0,
      series_points: matchPlayer.series_points || 0,
      series_avg_points: matchPlayer.series_avg_points || 0,
      total_points: matchPlayer.total_points || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this match player?')) {
      try {
        await matchPlayerApi.delete(id);
        fetchMatchPlayers(page, pageSize);
      } catch (error) {
        console.error('Error deleting match player:', error);
      }
    }
  };

  const handleRefreshPlayerStats = async (playerId: number) => {
    try {
      await playerApi.refreshStats(playerId);
      alert('Player stats refresh initiated. This may take a few moments.');
    } catch (error) {
      console.error('Error refreshing player stats:', error);
      alert('Failed to refresh player stats. Please try again later.');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedMatchPlayer(null);
    setDialogInitialValues(null);
  };

  const handleDialogSubmit = async (values: MatchPlayerFormValues) => {
    try {
      if (values.id) {
        await matchPlayerApi.update(values.id, values);
        fetchMatchPlayers(page, pageSize);
        handleDialogClose();
      }
    } catch (error) {
      console.error('Error updating match player:', error);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="secondary" onClick={() => navigate(`/ai-lineup?match=${matchId}`)}>
          AI Lineup Analysis
        </Button>
      </Box>
      <DataGrid
        rows={matchPlayers}
        columns={columns.map(col =>
          col.field === 'actions'
            ? {
                ...col,
                renderCell: (params: any) => (
                  <Box>
                    <Tooltip title="Edit">
                      <IconButton color="primary" size="small" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" size="small" onClick={() => handleDelete(params.row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh Player Stats">
                      <IconButton color="info" size="small" onClick={() => handleRefreshPlayerStats(params.row.player.id)}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
              }
            : col
        )}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50, page: 0 },
          },
          columns: {
            columnVisibilityModel: {
              match: false,
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={loading}
        density="compact"
      />
      <MatchPlayerDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        initialValues={dialogInitialValues || {
          id: 0,
          player: {} as Player,
          match: null,
          team: null,
          role: 'BAT',
          lineup_status: 'NOT_PLAYING',
          credits: 0,
          selected_perc: 0,
          captain_perc: 0,
          vice_captain_perc: 0,
          series_points: 0,
          series_avg_points: 0,
          total_points: 0,
        }}
        availablePlayers={availablePlayers}
        mode={selectedMatchPlayer ? 'edit' : 'add'}
      />
    </Box>
  );
};

export default MatchPlayersTable; 